import axios from "axios";

const SESSION_PREFIX = "showup.deleteAccount.";

export const DELETE_ACCOUNT_SESSION_KEYS = Object.freeze({
  token: `${SESSION_PREFIX}token`,
  userId: `${SESSION_PREFIX}userId`,
  phoneNumber: `${SESSION_PREFIX}phoneNumber`,
  dialCode: `${SESSION_PREFIX}dialCode`,
  fcmToken: `${SESSION_PREFIX}fcmToken`,
});

const apiBaseUrl = (process.env.VUE_APP_API_URL || "/").replace(/\/+$/, "");
const deleteAccountApi = axios.create({
  baseURL: `${apiBaseUrl}/`,
  headers: {
    "Content-Type": "application/json",
  },
});

// The admin API service sets an Authorization header on axios.defaults.
// axios.create() copies those defaults, so remove that copied header here.
// This client only receives a user token for the final deletion request.
delete deleteAccountApi.defaults.headers.common.Authorization;
delete deleteAccountApi.defaults.headers.common.authorization;

function getSessionValue(key) {
  try {
    return window.sessionStorage.getItem(key) || "";
  } catch (error) {
    return "";
  }
}

function setSessionValue(key, value) {
  try {
    window.sessionStorage.setItem(key, value);
  } catch (error) {
    // The flow still works in memory when browser storage is unavailable.
  }
}

function removeSessionValue(key) {
  try {
    window.sessionStorage.removeItem(key);
  } catch (error) {
    // Nothing else is required when browser storage is unavailable.
  }
}

function getApiMessage(data, fallback) {
  if (!data) return fallback;

  if (typeof data === "string") return data;

  return (
    data.Message ||
    data.message ||
    data.error_description ||
    (data.error && (data.error.message || data.error.Message)) ||
    fallback
  );
}

function assertApiSuccess(data, fallbackMessage) {
  const hasFailedCode =
    data && data.Code && data.Code.toString().toUpperCase() !== "P00001";
  const explicitlyFailed =
    data &&
    (data.success === false ||
      data.Success === false ||
      data.status === false ||
      data.Status === false);

  if (hasFailedCode || explicitlyFailed) {
    throw new Error(getApiMessage(data, fallbackMessage));
  }

  return data;
}

function normalizeRequestError(error, fallbackMessage) {
  if (error && !error.response && error.message) return error;

  const normalizedError = new Error(
    getApiMessage(
      error && error.response && error.response.data,
      fallbackMessage
    )
  );

  normalizedError.status =
    error && error.response ? error.response.status : undefined;

  return normalizedError;
}

function decodeJwtPayload(token) {
  try {
    const encodedPayload = token.split(".")[1];
    if (!encodedPayload) return {};

    const base64 = encodedPayload
      .replace(/-/g, "+")
      .replace(/_/g, "/")
      .padEnd(Math.ceil(encodedPayload.length / 4) * 4, "=");
    const binaryPayload = window.atob(base64);
    const jsonPayload = decodeURIComponent(
      Array.prototype.map
        .call(
          binaryPayload,
          (character) =>
            `%${`00${character.charCodeAt(0).toString(16)}`.slice(-2)}`
        )
        .join("")
    );

    return JSON.parse(jsonPayload);
  } catch (error) {
    return {};
  }
}

function findToken(data) {
  const containers = [
    data,
    data && data.Data,
    data && data.data,
    data && data.result,
    data && data.user,
    data && data.Data && data.Data.user,
    data && data.data && data.data.user,
  ];

  for (const container of containers) {
    if (!container || typeof container !== "object") continue;

    const token =
      container.token ||
      container.access_token ||
      container.accessToken ||
      container.auth_token;

    if (token) return token;
  }

  return "";
}

function findUserId(data, token) {
  const jwtPayload = decodeJwtPayload(token);
  const jwtUserId =
    jwtPayload._id || jwtPayload.user_id || jwtPayload.userId || jwtPayload.id;

  if (jwtUserId) return jwtUserId;

  const containers = [
    data && data.Data,
    data && data.data,
    data && data.result,
    data && data.user,
    data && data.Data && data.Data.user,
    data && data.data && data.data.user,
  ];

  for (const container of containers) {
    if (!container || typeof container !== "object") continue;

    const userId =
      container._id ||
      container.user_id ||
      container.userId ||
      container.id;

    if (userId) return userId;
  }

  return "";
}

function createPortalFcmToken() {
  const randomPart = Math.random()
    .toString(36)
    .slice(2);

  return `delete-account-portal-${Date.now()}-${randomPart}`;
}

export const deleteAccountSession = {
  get() {
    return {
      token: getSessionValue(DELETE_ACCOUNT_SESSION_KEYS.token),
      userId: getSessionValue(DELETE_ACCOUNT_SESSION_KEYS.userId),
      phoneNumber: getSessionValue(
        DELETE_ACCOUNT_SESSION_KEYS.phoneNumber
      ),
      dialCode: getSessionValue(DELETE_ACCOUNT_SESSION_KEYS.dialCode),
    };
  },

  savePhone(dialCode, phoneNumber) {
    setSessionValue(DELETE_ACCOUNT_SESSION_KEYS.dialCode, dialCode);
    setSessionValue(DELETE_ACCOUNT_SESSION_KEYS.phoneNumber, phoneNumber);
  },

  saveVerification(token, userId) {
    setSessionValue(DELETE_ACCOUNT_SESSION_KEYS.token, token);
    setSessionValue(DELETE_ACCOUNT_SESSION_KEYS.userId, userId);
  },

  getOrCreateFcmToken() {
    const existingToken = getSessionValue(
      DELETE_ACCOUNT_SESSION_KEYS.fcmToken
    );
    if (existingToken) return existingToken;

    const token = createPortalFcmToken();
    setSessionValue(DELETE_ACCOUNT_SESSION_KEYS.fcmToken, token);
    return token;
  },

  clearVerification() {
    removeSessionValue(DELETE_ACCOUNT_SESSION_KEYS.token);
    removeSessionValue(DELETE_ACCOUNT_SESSION_KEYS.userId);
  },

  clear() {
    Object.keys(DELETE_ACCOUNT_SESSION_KEYS).forEach((key) => {
      removeSessionValue(DELETE_ACCOUNT_SESSION_KEYS[key]);
    });
  },
};

export async function requestDeleteAccountOtp(dialCode, phoneNumber) {
  try {
    const response = await deleteAccountApi.post(
      "user/login",
      {
        dial_code: dialCode,
        phone_number: phoneNumber,
      }
    );

    return assertApiSuccess(
      response.data,
      "We could not send a verification code. Please try again."
    );
  } catch (error) {
    throw normalizeRequestError(
      error,
      "We could not send a verification code. Please try again."
    );
  }
}

export async function verifyDeleteAccountOtp(phoneNumber, otp, fcmToken) {
  try {
    const response = await deleteAccountApi.post(
      "user/verify-otp",
      {
        phone_number: phoneNumber,
        otp,
        fcm_token: fcmToken,
      }
    );
    const responseData = assertApiSuccess(
      response.data,
      "The verification code is invalid or has expired."
    );
    const token = findToken(responseData);
    const userId = findUserId(responseData, token);

    if (!token || !userId) {
      throw new Error(
        "Your identity was verified, but the account details were incomplete. Please try again."
      );
    }

    return { token, userId };
  } catch (error) {
    throw normalizeRequestError(
      error,
      "The verification code is invalid or has expired."
    );
  }
}

export async function deleteVerifiedAccount(userId, token) {
  try {
    const response = await deleteAccountApi.post(
      `user/delete-user/${encodeURIComponent(userId)}`,
      {},
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    return assertApiSuccess(
      response.data,
      "We could not delete your account. Please try again."
    );
  } catch (error) {
    throw normalizeRequestError(
      error,
      "We could not delete your account. Please try again."
    );
  }
}
